import React from "react";
import { Box, Paper, Typography, alpha, useTheme } from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import PerlLabelMedium from "../components/PerlLabelMedium";
import PerlLabelSmall from "../components/PerlLabelSmall";
import PerlButton from "../components/PerlButton";
import { quickCostLimitations } from "../constants/quickCostLimitations";

/**
 * InfoNotification Component
 *
 * Reusable notification/info banner with icon and message
 * Used to display contextual information or descriptions
 *
 * @param {Object} props
 * @param {string} props.title - The title/section heading to display
 * @param {string} props.message - The message/description to display
 * @param {React.ReactNode} [props.icon] - Optional custom icon (defaults to InfoIcon)
 * @param {boolean} [props.show] - Whether to show the notification (default: true)
 * @param {string} [props.variant] - Visual variant: "info" | "warning" | "success" (default: "info")
 * @param {boolean} [props.showButton] - Whether to show the "Let's Start" button (default: false)
 * @param {Function} [props.onButtonClick] - Callback when button is clicked
 * @param {string} [props.buttonText] - Custom button text (default: "Let's Start")
 * @param {boolean} [props.showLimitations] - Whether to show Quick Cost limitations
 */
const InfoNotification = ({
  title,
  message,
  icon,
  show = true,
  variant = "info",
  showButton = false,
  onButtonClick,
  buttonText = "Let's Start",
  showLimitations,
}) => {
  const theme = useTheme();
  const perlColors = theme.custom?.perlColors || {};
  const brandColor = perlColors.brandColor || "#ff6034";

  // Variant colors
  const variantColors = {
    info: brandColor,
    warning: "#ff9800",
    success: "#4caf50",
  };

  const accentColor = variantColors[variant] || brandColor;
  const shouldShowLimitations = showLimitations ?? title === "Build Product Profile";

  if (!show || !title) return null;

  if (shouldShowLimitations) {
    return (
      <Paper
        sx={{
          mb: 3,
          p: 2.25,
          bgcolor: perlColors.surface,
          border: `1px solid ${perlColors.border}`,
          borderLeft: `4px solid ${perlColors.textSecondary}`,
          borderRadius: "8px",
        }}
      >
        <Box
          sx={{
            p: 2,
            borderRadius: "8px",
            border: `1px solid ${alpha(accentColor, 0.28)}`,
            backgroundColor: alpha(accentColor, 0.06),
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <InfoIcon sx={{ color: accentColor, fontSize: 18 }} />
            <Typography
              sx={{
                color: perlColors.white,
                fontWeight: 700,
                fontSize: "0.9rem",
              }}
            >
              Limitations
            </Typography>
          </Box>
          <Box
            component="ul"
            sx={{
              m: 0,
              pl: 2.25,
              color: perlColors.textSecondary,
              "& li::marker": {
                color: accentColor,
              },
            }}
          >
            {quickCostLimitations.map((limitation) => (
              <Typography
                key={limitation}
                component="li"
                sx={{
                  color: perlColors.textSecondary,
                  fontSize: "0.8rem",
                  lineHeight: 1.55,
                  mb: 0.5,
                  pl: 0.5,
                  "&:last-of-type": {
                    mb: 0,
                  },
                }}
              >
                {limitation}
              </Typography>
            ))}
          </Box>
          {showButton && (
            <Box sx={{ mt: 2 }}>
              <PerlButton onClick={onButtonClick}>{buttonText}</PerlButton>
            </Box>
          )}
        </Box>
      </Paper>
    );
  }

  return (
    <Paper
      sx={{
        mb: 3,
        p: 2.25,
        bgcolor: perlColors.surface,
        border: `1px solid ${perlColors.border}`,
        borderLeft: `4px solid ${perlColors.textSecondary}`,
        borderRadius: "8px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: shouldShowLimitations ? "row" : "column" },
          alignItems: "flex-start",
          gap: shouldShowLimitations ? 3 : 0,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2 }}>
            {icon || (
              <InfoIcon
                sx={{
                  color: perlColors.white,
                  fontSize: "1.25rem",
                }}
              />
            )}
            <PerlLabelMedium sx={{ color: perlColors.white }}>{title}</PerlLabelMedium>
          </Box>
          <PerlLabelSmall
            sx={{
              color: perlColors.textSecondary,
              display: "block",
              mb: showButton ? 3 : 0,
            }}
          >
            {message}
          </PerlLabelSmall>
          {showButton && (
            <Box sx={{ alignSelf: "flex-start" }}>
              <PerlButton onClick={onButtonClick}>{buttonText}</PerlButton>
            </Box>
          )}
        </Box>
        {shouldShowLimitations && (
          <Box
            sx={{
              width: { xs: "100%", md: "46%" },
              maxWidth: { md: 680 },
              p: 2,
              borderRadius: "8px",
              border: `1px solid ${alpha(accentColor, 0.28)}`,
              backgroundColor: alpha(accentColor, 0.06),
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <InfoIcon sx={{ color: accentColor, fontSize: 18 }} />
              <Typography
                sx={{
                  color: perlColors.white,
                  fontWeight: 700,
                  fontSize: "0.9rem",
                }}
              >
                Limitations
              </Typography>
            </Box>
            <Box
              component="ul"
              sx={{
                m: 0,
                pl: 2.25,
                color: perlColors.textSecondary,
                "& li::marker": {
                  color: accentColor,
                },
              }}
            >
              {quickCostLimitations.map((limitation) => (
                <Typography
                  key={limitation}
                  component="li"
                  sx={{
                    color: perlColors.textSecondary,
                    fontSize: "0.8rem",
                    lineHeight: 1.55,
                    mb: 0.5,
                    pl: 0.5,
                    "&:last-of-type": {
                      mb: 0,
                    },
                  }}
                >
                  {limitation}
                </Typography>
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default InfoNotification;
